import type { Comment } from '@/@types/comment'
import { PostType } from '@/@types/post'
import { User } from '@/@types/user'
import {
	DELETE_COMMENT,
	LIKE_COMMENT,
	PIN_COMMENT,
	UNLIKE_COMMENT,
	UNPIN_COMMENT,
} from '@/constants/api'
import { generateCommentQueryKey, GetCommentParams } from '@/utils/fetchers/comment'
import { currentUserQueryOptions } from '@/utils/fetchers/user'
import usePost from '@/utils/hooks/usePost'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import queryClient from '@/utils/queryClient'
import { post } from '@/utils/request'
import { POST_TYPE_TO_ROUTE_MAPPING } from '@/utils/route'
import { UseMutationOptions } from '@tanstack/react-query'
import { cloneDeep, isNil, pick, set, update } from 'lodash-es'
import { MouseEvent, useCallback } from 'react'
import toast from 'react-hot-toast'

export const LIKE_COMMENT_TOGGLE_MUTATION_OPTIONS: UseMutationOptions<
	Comment,
	unknown,
	GetCommentParams,
	{
		prevData: Comment | undefined
	}
> = {
	mutationFn: async (data) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
		const comment = queryClient.getQueryData<Comment>(queryKey)
		if (isNil(comment)) {
			return Promise.reject()
		}

		return post(comment.liked ? LIKE_COMMENT : UNLIKE_COMMENT, {
			id: comment.id,
			targetType: comment.targetType,
		}).then((res) => res.data)
	},
	onMutate: async (data) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)

		// Cancel any outgoing refetches
		// (so they don't overwrite our optimistic update)
		await queryClient.cancelQueries({ queryKey })

		// Snapshot the previous value
		const previousComment = queryClient.getQueryData<Comment>(queryKey)

		// Optimistically update to the new value
		queryClient.setQueryData(queryKey, (prev: Comment | undefined) => {
			if (isNil(prev)) {
				return prev
			}
			const next = cloneDeep(prev)
			if (prev.liked) {
				update(next, 'likeCount', (likeCount: number) => likeCount - 1)
				set(next, 'liked', false)
			} else {
				update(next, 'likeCount', (likeCount: number) => likeCount + 1)
				set(next, 'liked', true)
				const currentUser = queryClient.getQueryData<User>(currentUserQueryOptions.queryKey)
				const isLikingSelf = next.user?.id === currentUser?.id

				if (isLikingSelf) {
					toast('😏', {
						position: 'bottom-center',
					})
				}
			}
			return next
		})

		// Return a context object with the snapshotted value
		return { prevData: previousComment }
	},
	// If the mutation fails,
	// use the context returned from onMutate to roll back
	onError: (err, data, context) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
		queryClient.setQueryData(queryKey, context?.prevData)
	},
}

export const PIN_COMMENT_TOGGLE_MUTATION_OPTIONS: UseMutationOptions<
	Comment,
	unknown,
	GetCommentParams,
	{
		prevData: Comment | undefined
	}
> = {
	mutationFn: async (data) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
		const comment = queryClient.getQueryData<Comment>(queryKey)
		if (isNil(comment)) {
			return Promise.reject()
		}
		return post(comment.isPinned ? PIN_COMMENT : UNPIN_COMMENT, {
			id: comment.id,
			targetType: comment.targetType,
		}).then((res) => res.data)
	},
	onMutate: async (data) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)

		await queryClient.cancelQueries({ queryKey })
		const previousComment = queryClient.getQueryData<Comment>(queryKey)

		// Optimistically update to the new value
		queryClient.setQueryData(queryKey, (prev: Comment | undefined) => {
			if (isNil(prev)) {
				return prev
			}
			const next = cloneDeep(prev)
			update(next, 'isPinned', (isPinned) => !isPinned)
			return next
		})

		// Return a context object with the snapshotted value
		return { prevData: previousComment }
	},
	// If the mutation fails,
	// use the context returned from onMutate to roll back
	onError: (err, data, context) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
		queryClient.setQueryData(queryKey, context?.prevData)
	},
}

export const DELETE_COMMENT_MUTATION_OPTIONS: UseMutationOptions<
	Comment,
	unknown,
	GetCommentParams,
	{
		prevData: Comment | undefined
	}
> = {
	mutationFn: async (data) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
		const comment = queryClient.getQueryData<Comment>(queryKey)
		if (isNil(comment)) {
			return Promise.reject()
		}
		return post(DELETE_COMMENT, {
			id: comment.id,
			targetType: comment.targetType,
		}).then((res) => res.data)
	},
	onMutate: async (data) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
		await queryClient.cancelQueries({ queryKey })

		// Snapshot the previous value
		const previousComment = queryClient.getQueryData<Comment>(queryKey)

		// Optimistically update to the new value
		queryClient.setQueryData(queryKey, (prev: Comment | undefined) => {
			if (isNil(prev)) {
				return prev
			}
			const next = cloneDeep(prev)
			set(next, 'deleted', true)
			return next
		})

		// Return a context object with the snapshotted value
		return { prevData: previousComment }
	},
	// If the mutation fails,
	// use the context returned from onMutate to roll back
	onError: (err, data, context) => {
		const queryKey = generateCommentQueryKey(pick(data, ['id', 'targetType']) as GetCommentParams)
		queryClient.setQueryData(queryKey, context?.prevData)
	},
}

export function useToCommentPage({ targetId, level, targetType, id }: Comment) {
	const postId = targetId
	const targetPost = usePost(targetId)
	const targetPostUserName = targetPost?.user?.username
	const navigate = useUniversalNavigate()

	const canToCommentPage = !!(targetPostUserName && postId && level <= 1)
	const toCommentPage = useCallback(
		(e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>) => {
			const type = targetType && POST_TYPE_TO_ROUTE_MAPPING[targetType as PostType]

			if (!canToCommentPage || window.getSelection()?.toString().length || !type) {
				return
			}
			e.stopPropagation()

			if (level <= 1) {
				navigate(
					{
						to: '/u/$username/$type/$id/$commentId',
						params: {
							username: targetPostUserName,
							id: postId,
							type,
							commentId: id,
						},
					},
					e
				)
			}
		},
		[canToCommentPage, id, level, postId, targetType, targetPostUserName, navigate]
	)

	return {
		canToCommentPage,
		toCommentPage,
	}
}
